import { Container } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { GameVisualAssets } from "../../assets/GameVisualAssets";
import { COIN_FOUNTAIN_TUNING } from "../../tuning/coinFountainTuning";
import { CoinFountainView } from "../coin-fountain/CoinFountainView";
import { SpineCoin } from "../coin-fountain/SpineCoin";
import { SpriteText, SpriteTextFitMode, hideSlotAttachments, playClipAndWait, setClip, socketContainerToBone } from "@slot/presentation";
import type { PresentationTaskRunner, SpineDriver, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap } from "@slot/game-shell";
import type { BigWinPort, LocalizationService } from "@slot/game-shell";

// Game tier id (server `levelIdentifier`) → the `big_wins` Spine tier group. `nice` has no entry, so
// `revealTier` returns false (titleless). `mega_win` is unused until a mega tier is added server-side.
const TIER_CLIP: Record<string, string> = { big: "big_win", huge: "huge_win", epic: "epic_win" };

/** `ModalLayer/GameModalContainer/BigWinPanel` geometry from the scene dump (authored y-up, stored y-down). */
const BIG_WIN_GEOMETRY = {
  // The resting number at the grid's centre (see ModalChrome's `content`), measured 2026-10-04. Landscape: not yet.
  content: { width: 2536, height: 1200, y: { desktop: 0, mobile: -200, mobile_landscape: 0 }, shiftY: { desktop: -270.3, mobile: -144.2, mobile_landscape: 0 } },
  titleSprites: { y: -354.4, spineScale: 1.59 },
  numbersSpine: { y: 246.9, scale: 0.971 },
  amountText: { y: 481.2, boxWidth: 869, boxHeight: 238, glyphScale: 3, maxCharacters: 24 },
} as const;

// TITLE tiers — the `big_wins` Spine (track 0). Each tier is two slots `<tier>`/`<tier>2`; the setup pose
// hides all, and each `<tier>/in` fades one in — so the view hides every tier then shows exactly one.
const TIER_SPINES = ["big_win", "mega_win", "huge_win", "epic_win"] as const;
const TIER_TRACK = 0;

// NUMBER — the `big_wins_number` Spine (its OWN skeleton). The digit `SpriteText` is socketed to the
// `number_placeholder` bone, so it rides `numbers/in` (pop-in 2.19→0.86→1) on enter + `numbers/out` on
// exit. The guide region is hidden; the digits fade via a light manual alpha tween (the socket rides
// the bone, not the faded guide slot). PURE SPINE — the count-up value is the presenter's (`setAmount`);
// there is NO count-up pulse / tier-punch / settle-bump (dropped when adopting big_wins_number).
const NUMBER_IN_CLIP = "numbers/in";
const NUMBER_OUT_CLIP = "numbers/out";
const NUMBER_GUIDE_SLOT = "number_placeholder";
const NUMBER_TRACK = 0;
const NUMBER_FADE_IN = 0.17; // matches numbers/in rgba (0 → 0.167)
const NUMBER_FADE_OUT = 0.23; // matches numbers/out rgba (0.167 → 0.4)

const CONTINUE_FADE_IN = 0.4;
const CONTINUE_FADE_OUT = 0.2;

// COINS — the fountain thrown from behind the title (`CoinFountainView`, tuned in `COIN_FOUNTAIN_TUNING`). The `coin`
// Spine's one clip is the coin's spin: a half-turn, the face squeezed to its edge and back.
const COIN_SKELETON = "animations/spine/coin.skeleton";
const COIN_SPIN_CLIP = "animation";

/**
 * The big-win view behind `BigWinPort`, SPINE-DRIVEN (both title AND number):
 *  - TITLE tiers + glow: the `big_wins` Spine (`skeleton`, track 0). `revealTier` shows one tier + plays
 *    `<tier>/in`; the exit plays `<tier>/out`.
 *  - NUMBER: the `big_wins_number` Spine (`numberSkeleton`). The digit `SpriteText` (`numberAnimRoot`) is
 *    socketed to `number_placeholder`, so it rides `numbers/in` (pop-in) on enter + `numbers/out` on exit;
 *    a light manual alpha fade reveals/hides the digits (the socket rides the bone, not the faded guide).
 *    The count-up value is the presenter's (`setAmount`) — there is NO pulse/punch/settle (pure Spine), so
 *    `startAmountPulse` / `playAmountSettleBump` are no-ops.
 *  - COINS: a `CoinFountainView` on the chrome's backdrop (over the dim, behind the title and the number), thrown
 *    from behind the title. The first titled reveal starts it and every tier after it steps it up; it runs through
 *    "tap to continue", stops throwing on exit while the coins in flight fade, and is emptied on hide. A nice win
 *    has no title, so no coins.
 *
 * UI chrome on its own clock: every fade rides the chrome runner (through `ModalChrome.fade`)
 * and every clip plays at that runner's time scale.
 */
export class BigWinView extends Container implements BigWinPort {
  private readonly amountSpriteText: SpriteText | null;
  private readonly skipButton: Container;
  private readonly chrome: ModalChrome;
  private readonly runner: PresentationTaskRunner;
  /** big_wins Spine — per-tier in/out clips + glow (TITLE). */
  private readonly skeleton: Spine | null;
  /** big_wins_number Spine — numbers/in + out (NUMBER); the digits are socketed to its number_placeholder. */
  private readonly numberSkeleton: Spine | null;
  /** The win-amount SpriteText (or its stand-in) — rides numbers/in-out on the number Spine's bone. */
  private readonly numberAnimRoot: Container;
  private readonly continueTextRoot: Container;
  /** The coin fountain behind the title; null without the coin skeleton. */
  private readonly coinFountain: CoinFountainView | null;
  private currentTier: string | null = null;
  private isVisible = false;

  public constructor(input: {
    assets: GameVisualAssets;
    viewport: ViewportStore;
    localization: LocalizationService;
    spineDriver: SpineDriver;
    runner: PresentationTaskRunner;
  }) {
    super({ label: "BigWinPanel" });
    const g = BIG_WIN_GEOMETRY;
    this.runner = input.runner;
    this.chrome = new ModalChrome({
      label: "root",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: {
        ...g.content,
        limits: { desktop: { maxWidth: 2536 }, mobile: { maxWidth: 2536 }, mobile_landscape: {} },
      },
    });
    this.addChild(this.chrome);
    this.skipButton = this.chrome.skipButton;
    this.continueTextRoot = this.chrome.continueTextRoot;

    const animatedContainer = new Container({ label: "AnimatedContainer" });
    this.chrome.content.addChild(animatedContainer);

    const spawn = (path: string, label: string, y: number, scale: number, parent: Container): Spine | null => {
      const data = input.assets.skeletons.get(path);
      if (!data) return null;
      const spine = new Spine({ skeletonData: data, autoUpdate: false });
      spine.label = label;
      spine.position.set(0, y);
      spine.scale.set(scale);
      input.spineDriver.attach(spine);
      parent.addChild(spine);
      return spine;
    };
    const titleSprites = new Container({ label: "TitleSprites" });
    titleSprites.position.set(0, g.titleSprites.y);
    animatedContainer.addChild(titleSprites);
    this.skeleton = spawn("animations/spine/big_wins.skeleton", "spine", 0, g.titleSprites.spineScale, titleSprites);
    this.numberSkeleton = spawn("animations/spine/big_wins_number.skeleton", "numbers_spine", g.numbersSpine.y, g.numbersSpine.scale, animatedContainer);
    // The guide region is hidden for good (strips the skin attachment, so setup pose can't bring it back).
    if (this.numberSkeleton) hideSlotAttachments(this.numberSkeleton, NUMBER_GUIDE_SLOT);

    const font = input.assets.fonts.goldenFont;
    this.amountSpriteText = font
      ? new SpriteText({ font, maxCharacters: g.amountText.maxCharacters, glyphScale: g.amountText.glyphScale, fitMode: SpriteTextFitMode.Width, boxWidth: g.amountText.boxWidth, boxHeight: g.amountText.boxHeight, label: "SpriteText" })
      : null;
    this.numberAnimRoot = this.amountSpriteText ?? new Container({ label: "SpriteText" });
    this.numberAnimRoot.position.set(0, g.amountText.y);
    this.numberAnimRoot.alpha = 0;
    animatedContainer.addChild(this.numberAnimRoot);
    // The digits ride the number Spine's `number_placeholder` bone.
    if (this.numberSkeleton) socketContainerToBone(this.numberSkeleton, "number_placeholder", this.numberAnimRoot, 1);

    // Anchored on the title, so the coins come from behind it and keep their size against it on every shape.
    const coinData = input.assets.skeletons.get(COIN_SKELETON);
    this.coinFountain = coinData
      ? new CoinFountainView({
          createCoin: () => new SpineCoin({ skeletonData: coinData, spinClip: COIN_SPIN_CLIP, spineDriver: input.spineDriver, runner: input.runner }),
          tuning: COIN_FOUNTAIN_TUNING.fountain,
          anchor: titleSprites,
          viewport: input.viewport,
          runner: input.runner,
        })
      : null;
    if (this.coinFountain) this.chrome.backdrop.addChild(this.coinFountain);

    this.hideImmediate();
  }

  /** Hidden with a hard reset: both Spines cleared, no coins, digit/hint fades killed. */
  public hideImmediate(): void {
    this.currentTier = null;
    this.skeleton?.state.clearTracks();
    this.numberSkeleton?.state.clearTracks();
    this.chrome.stopFade(this.numberAnimRoot);
    this.chrome.stopFade(this.continueTextRoot);
    this.resetCoins();
    this.chrome.visible = false;
    this.isVisible = false;
  }

  public showImmediate(): void {
    this.chrome.visible = true;
    this.isVisible = true;
  }

  /** Minimal opening state — every tier hidden, hint + digits hidden (no setup-pose flash on show), no coins. */
  public hideTitles(): void {
    this.currentTier = null;
    if (this.skeleton) for (const t of TIER_SPINES) hideTier(this.skeleton, t);
    this.chrome.stopFade(this.continueTextRoot);
    this.continueTextRoot.alpha = 0;
    this.chrome.stopFade(this.numberAnimRoot);
    this.numberAnimRoot.alpha = 0;
    this.resetCoins();
  }

  /**
   * Reveal/upgrade the title for `tierId`. Returns false for a titleless tier (`nice`) — no Spine tier.
   * For a titled tier: the coins start or step up; hide all tiers, show this one, play its `<tier>/in`. The
   * Spine handles the first reveal and an upgrade identically, so `animateEnter` isn't needed here.
   */
  public revealTier(tierId: string, _animateEnter: boolean): boolean {
    const tier = (tierId ?? "").toLowerCase();
    const spineTier = TIER_CLIP[tier];
    if (!spineTier) return false;
    const emission = COIN_FOUNTAIN_TUNING.emissionByTier[tier];
    if (emission) this.coinFountain?.run(emission);
    const sk = this.skeleton;
    if (!sk) return true;
    for (const t of TIER_SPINES) hideTier(sk, t);
    sk.skeleton.setAttachment(spineTier, spineTier);
    sk.skeleton.setAttachment(`${spineTier}2`, spineTier);
    this.currentTier = spineTier;
    setClip(sk, this.runner, `${spineTier}/in`, false, TIER_TRACK);
    return true;
  }

  public setAmount(text: string): void {
    this.amountSpriteText?.setText(text);
  }

  public setSkipVisible(visible: boolean): void {
    this.skipButton.visible = visible;
  }

  public get amountOmitsCurrencySymbol(): boolean {
    return this.amountSpriteText !== null;
  }

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.skipButton, onPress);
  }

  public getIsVisible(): boolean {
    return this.isVisible;
  }

  // ── BigWinPort animation — Spine title (revealTier) + Spine number (enter/exit); pulse/settle no-op ──

  /** NUMBER enter: seat the number Spine + play `numbers/in` (digits pop via the bone) + fade in. */
  public async playEnterAnimation(): Promise<void> {
    const nsk = this.numberSkeleton;
    if (nsk) {
      nsk.state.clearTracks();
      nsk.skeleton.setToSetupPose();
      setClip(nsk, this.runner, NUMBER_IN_CLIP, false, NUMBER_TRACK);
    }
    await this.chrome.fade(this.numberAnimRoot, 1, NUMBER_FADE_IN);
  }

  /**
   * Exit: tier `<tier>/out` + number `numbers/out` (digits shrink via the bone) + digit + hint fade-out; the coins
   * stop coming and the ones in flight fade (a nice win never started them, so its exit stays as short as before).
   */
  public async playExitAnimation(): Promise<void> {
    const waits: Promise<unknown>[] = [this.chrome.fade(this.continueTextRoot, 0, CONTINUE_FADE_OUT)];
    if (this.skeleton && this.currentTier) waits.push(playClipAndWait(this.skeleton, this.runner, `${this.currentTier}/out`, { track: TIER_TRACK }));
    if (this.numberSkeleton) setClip(this.numberSkeleton, this.runner, NUMBER_OUT_CLIP, false, NUMBER_TRACK);
    waits.push(this.chrome.fade(this.numberAnimRoot, 0, NUMBER_FADE_OUT));
    if (this.coinFountain?.isRunning) {
      this.coinFountain.stopThrowing();
      waits.push(this.chrome.fade(this.coinFountain, 0, COIN_FOUNTAIN_TUNING.exitFadeSeconds));
    }
    await Promise.all(waits);
  }

  /** Count-up pulse — DROPPED with the pure-Spine number (the presenter still calls it). No-op. */
  public startAmountPulse(): void {
    /* pure Spine: numbers/in owns the enter pop; no count-up pulse */
  }

  /** Settle bump — DROPPED with the pure-Spine number. No-op. */
  public playAmountSettleBump(): Promise<void> {
    return Promise.resolve();
  }

  /** Fade the "tap to continue" hint in — the presenter calls this once the count-up completes (titled wins). */
  public fadeInContinueText(): Promise<void> {
    this.chrome.stopFade(this.continueTextRoot);
    this.continueTextRoot.alpha = 0;
    return this.chrome.fade(this.continueTextRoot, 1, CONTINUE_FADE_IN);
  }

  public dispose(): void {
    this.coinFountain?.dispose();
    this.chrome.dispose();
    this.destroy({ children: true });
  }

  /** No coins, none to come, no fade on them: the fountain ready for the next titled reveal. */
  private resetCoins(): void {
    if (!this.coinFountain) return;
    this.chrome.stopFade(this.coinFountain);
    this.coinFountain.clear();
    this.coinFountain.alpha = 1;
  }
}

/** Hide a tier group (both slots) — used to clear every tier before showing the active one. */
function hideTier(spine: Spine, tier: string): void {
  spine.skeleton.findSlot(tier)?.setAttachment(null);
  spine.skeleton.findSlot(`${tier}2`)?.setAttachment(null);
}
